import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router";
import { fetchJson, Loading } from "../../utils";
import PopularVideoStories from "../../blocks/pupular-video-stories";

type Inputs = {
    password: string;
    password_confirmation: string;
}

type PasswordResetConfirmResponse = {
    success: boolean;
    message?: string;
    field?: keyof Inputs;
};

export const PasswordResetConfirm = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isValidHash, setIsValidHash] = useState(false);
    const [error, setError] = useState<string>();

    const {
        register,
        handleSubmit,
        formState: { errors },
        setError: setFormError,
        watch
    } = useForm<Inputs>();

    useEffect(() => {
        const validateHash = async () => {
            try {
                const { json } = await fetchJson<{ valid: boolean }>(`/api/password-reset/validate`, {
                    method: 'POST',
                    body: JSON.stringify({ token })   
                });
                setIsValidHash(json.valid);
            } catch (e) {
                setError("Invalid or expired password reset link");
            } finally {
                setLoading(false);
            }
        };

        validateHash();
    }, [token]);

    const onSubmit: SubmitHandler<Inputs> = async (data) => {
        setSubmitting(true);
        try {
            const { json } = await fetchJson<PasswordResetConfirmResponse>(`/api/password-reset/confirm`, {
                method: 'POST',
                body: JSON.stringify({
                    ...data,
                    token
                })
            });

            if (json.success) {
                navigate('/sign-in', { state: { message: 'Your password has been successfully reset. Please sign in with your new password.' } });
            } else if (json.field && json.message) {
                setFormError(json.field, {
                    type: 'manual',
                    message: json.message
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <section className="section">
                <div className="inside-wrapper">
                    <h1 className="section__title">Validating Reset Link</h1>
                    <div className="signbox">
                        <div className="signbox__form form">
                            <Loading/>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (!isValidHash || error) {
        return (
            <section className="section">
                <div className="inside-wrapper">
                    <h1 className="section__title">Invalid Reset Link</h1>
                    <div className="signbox">
                        <div className="signbox__form form">
                            <p>{error || "This password reset link is invalid or has expired."}</p>
                            <div className="form__row form__row_smallgap form__note">
                                <Link to="/password-reset">Request a new password reset</Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <>
            <section className="section">
                <div className="inside-wrapper">
                    <h1 className="section__title">Set New Password</h1>
                    <div className="signbox">
                        <div className="signbox__form form">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className={`form__row ${errors.password && 'is-error'}`}>
                                    <label className="form__label">
                                        New Password
                                    </label>
                                    <div className="form__input-holder">
                                        <input 
                                            type="password" 
                                            className="form__input" 
                                            {...register('password', {
                                                required: 'Password is required',
                                                minLength: {
                                                    value: 6,
                                                    message: 'Password must be at least 6 characters long'
                                                }
                                            })} 
                                        />
                                    </div>
                                    {errors.password && <div className="form__alert">{errors.password.message}</div>}
                                </div>
                                <div className={`form__row ${errors.password_confirmation && 'is-error'}`}>
                                    <label className="form__label">
                                        Confirm New Password
                                    </label>
                                    <div className="form__input-holder">
                                        <input 
                                            type="password" 
                                            className="form__input" 
                                            {...register('password_confirmation', {
                                                required: 'Please confirm your password',
                                                validate: (val: string) => {
                                                    if (watch('password') != val) {
                                                        return "Passwords do not match";
                                                    }
                                                }
                                            })} 
                                        />
                                    </div>
                                    {errors.password_confirmation && <div className="form__alert">{errors.password_confirmation.message}</div>}
                                </div>
                                <div className="form__row form__row_smallgap form__btns">
                                    <button 
                                        className="btn btn_cta btn_lg btn_extrapad" 
                                        type="submit" 
                                        disabled={submitting}
                                    >
                                        Change Password
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
            <PopularVideoStories />
        </>
    );
};

export default PasswordResetConfirm;